import React, { useEffect, useId, useState } from 'react';

const HEX_FULL = /^#[0-9A-Fa-f]{6}$/;
const HEX_SHORT = /^#[0-9A-Fa-f]{3}$/;

function expandShortHex(value: string): string {
  const raw = value.slice(1);
  return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
}

function normalizeHex(value: string): string | null {
  const trimmed = value.trim();
  if (HEX_FULL.test(trimmed))
  {
    return trimmed.toLowerCase();
  }

  if (HEX_SHORT.test(trimmed))
  {
    return expandShortHex(trimmed).toLowerCase();
  }

  return null;
}

interface ColorPickerFieldProps {
  label: string;
  value: string;
  onChange: (hex: string) => void;
  fallback?: string;
}

export const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  label,
  value,
  onChange,
  fallback = '#2563eb',
}) => {
  const inputId = useId();
  const resolvedValue = normalizeHex(value) ?? fallback;
  const [textValue, setTextValue] = useState(resolvedValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const next = normalizeHex(value) ?? fallback;
    setTextValue(next);
    setError(null);
  }, [value, fallback]);

  const applyHex = (raw: string) => {
    const normalized = normalizeHex(raw);
    if (!normalized)
    {
      setError('Informe uma cor HEX válida (ex.: #2563eb)');
      return;
    }

    setError(null);
    setTextValue(normalized);
    onChange(normalized);
  };

  return (
    <div>
      <label htmlFor={inputId} className="block text-sm text-gray-700 mb-1">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <label
          title="Escolher cor"
          className="relative h-10 w-10 flex-shrink-0 cursor-pointer overflow-hidden rounded-md border border-gray-300 shadow-sm focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-1"
          style={{ backgroundColor: resolvedValue }}
        >
          <span className="sr-only">Escolher cor</span>
          <input
            type="color"
            value={resolvedValue}
            onChange={e => applyHex(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
        </label>
        <input
          id={inputId}
          type="text"
          value={textValue}
          onChange={e => {
            const next = e.target.value;
            setTextValue(next);
            const normalized = normalizeHex(next);
            if (normalized)
            {
              setError(null);
              onChange(normalized);
            }
            else
            {
              setError('Informe uma cor HEX válida (ex.: #2563eb)');
            }
          }}
          onBlur={() => {
            const normalized = normalizeHex(textValue);
            if (!normalized)
            {
              setTextValue(resolvedValue);
              setError(null);
              return;
            }

            setTextValue(normalized);
            setError(null);
            if (normalized !== resolvedValue)
            {
              onChange(normalized);
            }
          }}
          spellCheck={false}
          className={`w-full border rounded-md px-3 py-2 text-sm font-mono ${
            error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'
          }`}
          placeholder="#2563eb"
        />
      </div>
      {error ? (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      ) : null}
    </div>
  );
};
